// @ts-strict-ignore
import * as React from 'react';
import {
    IntegrationCapability,
    IntegrationType,
    AllIntegrationTypes,
    IntegrationErrorType,
    SettingType,
    IntegrationInstallationId,
    Integration,
    OrgTag,
} from '../types';
import { RootState } from '../reducers/reducers';
import { ConnectedProps, connect } from 'react-redux';
import {
    getError,
    getIsLoading,
    getUpdated,
    getAllAppsInOrg,
    getAppGroupsForOrgId,
    allSupportedIntegrationTypesForCapability,
    descriptionForCapability,
    displayNameForCapability,
} from '../helpers';
import { RouteComponentProps } from 'react-router-dom';
import { AppRouteComponentMatchParams, IntegrationRouteProps } from '../typescriptHelpers';
import qs from 'query-string';
import {
    tryCreateIntegrationRequest,
    CreateIntegrationRequest,
    UpdateIntegrationRequest,
    tryUpdateIntegrationRequest,
    CreateIntegrationRequestDataType,
    IntegrationRequest,
    tryDeleteIntegration,
    UpdateIntegrationRequestDataType,
    RefreshIntegrationRequest,
    tryRefreshIntegrationRequest,
    tryUpdateIntegrationAlias,
    UpdateIntegrationAliasRequest,
    TRY_UPDATE_INTEGRATION_ALIAS,
} from '../actions/integrations';
import {
    AuthUpdatable,
    IntegrationDeletable,
    IntegrationRefreshable,
    IntegrationUpdatable,
} from '../actions/updatable';
import {
    createAppSettingsRouteWithSettingType,
    createAppSettingsRoute,
    SettingsScrollTo,
} from '../helpers/routeHelpers';
import {
    authTypeForIntegrationType,
    displayNameForIntegrationType,
    extractIntegrationRequestFromRedirect,
    integrationCapabilitiesForPlatform,
    integrationDeletableForIntegrationType,
    IntegrationCreateOrRefreshRequestOrError,
    integrationUpdatableForIntegrationType,
} from '../helpers/integrationHelpers';
import IntegrationsForCapabilityComponent from './IntegrationsForCapabilityComponent';
import ConnectedIntegrationsComponent from './ConnectedIntegrationsComponent';
import { forIn } from 'lodash';
import IntegrationConfiguration from './IntegrationConfiguration';
import ReleaseBranchPatternsComponent from './ReleaseBranchPatternsComponent';
import ReleaseTagPatternsComponent from './ReleaseTagPatternsComponent';
import FeatureAffiliationsComponent from './FeatureAffiliationsComponent';
import { AppSettingsLocationState } from '../helpers/helperTypes';
import { TRY_REFRESH_INTEGRATIONS } from '../actions/appSettings';
import Text from 'antd/lib/typography/Text';
import Title from 'antd/lib/typography/Title';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import { allReleasePropertiesFrom } from '../helpers/trackingHelpers';
import * as AnalyticEvents from '../utils/analytics';
import AdditionalBranchesComponent from './AdditionalBranchesComponent';
import {
    selectDestinationInfoByDestinationStepConfigId,
    selectOrg,
    selectIsRunwayOrg,
    selectApp,
    selectStepGraph,
} from '../selectors/selectors';
import { globalMessage } from '../utils/globalMessage';
import { PlanRestrictedFeatureWrapper } from './planRestrictions/PlanRestrictedFeatureWrapper';
import Collapse from 'antd/lib/collapse';
import AutoComplete from 'antd/lib/auto-complete';
import Input from 'antd/lib/input';
import { SearchOutlined } from '@ant-design/icons';
import { Destination } from '@/types/destinations';
import { WorkflowStepGraph } from '@/types/workflow';
import { displayStringForDestination } from '@/helpers/displayHelpers';
import { antGrey } from './styles/globalStyles';

const { Panel } = Collapse;

const normalizeSearchText = (text?: string | null) => text?.trim().toLowerCase() ?? '';

const knownDestinationTags = (tags: string[] | undefined, tagById: Record<string, OrgTag>) =>
    (tags ?? []).filter((tagId) => !!tagById[tagId]);

const destinationDisplayName = (destination: Destination) =>
    displayStringForDestination(destination.platform, destination.appStoreType, destination.name, destination.appName);

const destinationWithOrgTagMembership = (
    destination: Destination,
    orgTags: OrgTag[],
    tagById: Record<string, OrgTag>,
): Destination => {
    const destinationIds = new Set([destination.destinationId, destination.stepConfigId].filter(Boolean));
    const tagIdsFromOrgTagMembership = orgTags
        .filter((tag) => tag.destinations.some((destinationId) => destinationIds.has(destinationId)))
        .map((tag) => tag.id);

    return {
        ...destination,
        tags: Array.from(new Set([...knownDestinationTags(destination.tags, tagById), ...tagIdsFromOrgTagMembership])),
    };
};

export const destinationsByIntegrationInstallationId = (
    stepGraph: WorkflowStepGraph,
    destinationInfoByDestinationStepConfigId: Record<string, Destination>,
    orgTags: OrgTag[],
): Record<IntegrationInstallationId, Destination[]> => {
    const tagById = orgTags.reduce<Record<string, OrgTag>>((acc, tag) => {
        acc[tag.id] = tag;
        return acc;
    }, {});
    const result: Record<IntegrationInstallationId, Destination[]> = {};

    stepGraph?.stepGroups.forEach((stepGroup) => {
        stepGroup.archetypeGroups.forEach((archetypeGroup) => {
            archetypeGroup.steps.forEach((step) => {
                const integrationInstallationIds = step.integrationInstallationIds ?? [];
                if (integrationInstallationIds.length === 0) {
                    return;
                }

                const destinationStepConfigIds =
                    step.destinationStepConfigIds?.length > 0 ? step.destinationStepConfigIds : [step.stepConfigId];

                integrationInstallationIds.forEach((integrationInstallationId) => {
                    const destinationsByStepConfigId = new Map(
                        (result[integrationInstallationId] ?? []).map((destination) => [
                            destination.stepConfigId,
                            destination,
                        ]),
                    );

                    destinationStepConfigIds.forEach((destinationStepConfigId) => {
                        const destination = destinationInfoByDestinationStepConfigId[destinationStepConfigId];
                        if (destination) {
                            destinationsByStepConfigId.set(
                                destination.stepConfigId,
                                destinationWithOrgTagMembership(destination, orgTags, tagById),
                            );
                        }
                    });

                    result[integrationInstallationId] = Array.from(destinationsByStepConfigId.values());
                });
            });
        });
    });

    return result;
};

const searchableTextForIntegration = (
    integration: Integration,
    integrationDestinations: Destination[],
    tagById: Record<string, OrgTag>,
) => {
    const tagText = integrationDestinations.reduce<string[]>((acc, destination) => {
        knownDestinationTags(destination.tags, tagById).forEach((tagId) => {
            acc.push(tagId);
            const tagName = tagById[tagId]?.tagName;
            if (tagName) {
                acc.push(tagName);
            }
        });
        return acc;
    }, []);
    const destinationText = integrationDestinations.reduce<string[]>((acc, destination) => {
        acc.push(destinationDisplayName(destination), destination.name);
        if (destination.appName) {
            acc.push(destination.appName);
        }
        return acc;
    }, []);

    return [
        integration.name,
        displayNameForIntegrationType(integration.id),
        integration.alias,
        ...destinationText,
        ...tagText,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
};

const integrationSearchOptions = (
    integrations: Integration[],
    destinationsByIntegrationId: Record<IntegrationInstallationId, Destination[]>,
    orgTags: OrgTag[],
    searchValue: string,
) => {
    const normalizedSearch = normalizeSearchText(searchValue);
    if (!normalizedSearch) {
        return [];
    }

    const tagById = orgTags.reduce<Record<string, OrgTag>>((acc, tag) => {
        acc[tag.id] = tag;
        return acc;
    }, {});
    const optionsByKey = new Map<string, { value: string; label: React.ReactNode }>();

    const addOption = (type: string, value?: string | null) => {
        const trimmedValue = value?.trim();
        if (!trimmedValue) {
            return;
        }
        if (normalizedSearch && !trimmedValue.toLowerCase().includes(normalizedSearch)) {
            return;
        }

        const key = `${type}:${trimmedValue.toLowerCase()}`;
        if (!optionsByKey.has(key)) {
            optionsByKey.set(key, {
                value: trimmedValue,
                label: (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                        <span>{trimmedValue}</span>
                        <Text type="secondary">{type}</Text>
                    </div>
                ),
            });
        }
    };

    integrations.forEach((integration) => {
        const integrationDestinations = destinationsByIntegrationId[integration.installationId] ?? [];
        addOption('Integration', integration.name);
        addOption('Integration', displayNameForIntegrationType(integration.id));
        addOption('Integration', integration.alias);

        integrationDestinations.forEach((destination) => {
            addOption('Destination', destinationDisplayName(destination));
            knownDestinationTags(destination.tags, tagById).forEach((tagId) => {
                addOption('Tag', tagById[tagId]?.tagName);
            });
        });
    });

    return Array.from(optionsByKey.values()).sort((a, b) => a.value.localeCompare(b.value));
};

interface State {
    showConfigurationModal: boolean;
    integrationId?: IntegrationInstallationId;
    closable: boolean;
    searchValue: string;
}

class Integrations extends React.Component<Props, State> {
    featureAffiliations: React.RefObject<HTMLDivElement>;
    releaseBranchPatterns: React.RefObject<HTMLDivElement>;
    releaseTagPatterns: React.RefObject<HTMLDivElement>;
    capabilitiesRefs: { [key: string]: React.RefObject<HTMLDivElement> } = {};

    constructor(props) {
        super(props);

        this.updateIntegration = this.updateIntegration.bind(this);
        this.addIntegration = this.addIntegration.bind(this);
        this.deleteIntegration = this.deleteIntegration.bind(this);
        this.updateIntegrationAlias = this.updateIntegrationAlias.bind(this);

        this.featureAffiliations = React.createRef();
        this.releaseBranchPatterns = React.createRef();
        this.releaseTagPatterns = React.createRef();

        this.state = {
            showConfigurationModal: false,
            integrationId: null,
            closable: false,
            searchValue: '',
        };
    }

    componentDidMount() {
        if (
            this.props.integrationCreateOrRefreshRequestOrError &&
            this.props.integrationCreateOrRefreshRequestOrError.error
        ) {
            // Redirect error
            globalMessage.error(this.props.integrationCreateOrRefreshRequestOrError.error);
        }

        if (
            this.props.integrationCreateOrRefreshRequestOrError &&
            this.props.integrationCreateOrRefreshRequestOrError.createRequest &&
            this.props.integrationCreateOrRefreshRequestOrError.authType
        ) {
            const authType = this.props.integrationCreateOrRefreshRequestOrError.authType;
            const createRequest = this.props.integrationCreateOrRefreshRequestOrError.createRequest;
            this.props.addIntegration(authType, createRequest);

            // Clear access code from query params
            this.props.history.replace(this.props.appSettingsIntegrationsRoute);
        } else if (
            this.props.integrationCreateOrRefreshRequestOrError &&
            this.props.integrationCreateOrRefreshRequestOrError.refreshRequest &&
            this.props.integrationCreateOrRefreshRequestOrError.integrationRefreshable
        ) {
            const integrationUpdatable = this.props.integrationCreateOrRefreshRequestOrError.integrationRefreshable;
            const refreshRequest = this.props.integrationCreateOrRefreshRequestOrError.refreshRequest;
            this.props.refreshIntegration(integrationUpdatable, refreshRequest);
            // Clear access code from query params
            this.props.history.replace(this.props.appSettingsIntegrationsRoute);
        }

        if (this.props.scrollTo) {
            switch (this.props.scrollTo) {
                case SettingsScrollTo.FeatureAffiliations:
                    this.featureAffiliations.current?.scrollIntoView(true);
                    break;
                case SettingsScrollTo.ReleaseBranchPatterns:
                    this.releaseBranchPatterns.current?.scrollIntoView(true);
                    break;
                case SettingsScrollTo.ReleaseTagPatterns:
                    this.releaseTagPatterns.current?.scrollIntoView(true);
                    break;
                default: {
                    const integrationCapability = Object.values(IntegrationCapability).find(
                        (cap) => cap === (this.props.scrollTo as string),
                    );
                    if (integrationCapability) {
                        this.capabilitiesRefs[integrationCapability].current?.scrollIntoView({
                            block: 'start',
                            inline: 'start',
                        });
                    }
                    break;
                }
            }
        }

        AnalyticEvents.track(AnalyticEvents.Event.ViewIntegrations, this.props.trackingProperties);
    }

    componentDidUpdate(prevProps: Props) {
        // New integration errors
        forIn(this.props.addIntegrationFailed, (errorMessage, integrationType) => {
            const prevError = prevProps.addIntegrationFailed[integrationType];

            if (!prevError && errorMessage) {
                globalMessage.error(errorMessage);
            }
        });

        // New integration success
        forIn(this.props.addIntegrationSucceeded, (updated, integrationId) => {
            const prevValue = prevProps.addIntegrationSucceeded[integrationId];

            if (!prevValue && updated) {
                const integration = this.props.integrations.find(
                    (integration) => integration.installationId === integrationId,
                );

                if (integration && !integration.integrationError) {
                    const integrationDisplayName = displayNameForIntegrationType(integration.id);
                    globalMessage.success(`${integrationDisplayName} integration added!`);
                } else if (
                    integration &&
                    integration.integrationError?.status === IntegrationErrorType.IncompleteData
                ) {
                    // Incomplete, show configuration modal
                    this.setState({
                        showConfigurationModal: true,
                        integrationId: integration.installationId,
                        closable: false,
                    });
                } else if (integration && integration.integrationError) {
                    globalMessage.error(integration.integrationError.message);
                } else {
                    globalMessage.error('Something went wrong, please try again');
                }
            }
        });

        // Update integration failure

        forIn(this.props.updateIntegrationFailed, (errorMessage, integrationId) => {
            const prevError = prevProps.updateIntegrationFailed[integrationId];

            if (!prevError && errorMessage) {
                globalMessage.error(errorMessage);
            }
        });

        // Update integration success
        forIn(this.props.updateIntegrationSucceeded, (updated, integrationId: IntegrationInstallationId) => {
            const prevValue = prevProps.updateIntegrationSucceeded[integrationId];

            if (!prevValue && updated) {
                const integration = this.props.integrations.find(
                    (integration) => integration.installationId === integrationId,
                );

                if (integration && !integration.integrationError) {
                    const integrationDisplayName = displayNameForIntegrationType(integration.id);
                    globalMessage.success(`${integrationDisplayName} integration updated!`);
                } else if (
                    integration &&
                    integration.integrationError?.status === IntegrationErrorType.IncompleteData
                ) {
                    globalMessage.warning('Please finish configuration of this integration.');
                }

                this.setState({
                    showConfigurationModal: false,
                    integrationId: null,
                });
            }
        });

        // Delete integration failure
        forIn(this.props.deleteIntegrationError, (errorMessage, integrationType) => {
            const prevValue = prevProps.deleteIntegrationError[integrationType];

            if (!prevValue && errorMessage) {
                globalMessage.error(errorMessage);
            }
        });

        // Delete integration success
        forIn(this.props.deleteIntegrationSucceeded, (updated, integrationType) => {
            const prevValue = prevProps.deleteIntegrationSucceeded[integrationType];

            if (!prevValue && updated) {
                const integrationDisplayName = displayNameForIntegrationType(integrationType as IntegrationType);

                globalMessage.success(`${integrationDisplayName} integration removed.`);
            }
        });

        // Refresh integrations

        if (!prevProps.refreshIntegrationsSuccess && this.props.refreshIntegrationsSuccess) {
            globalMessage.success('Integrations refreshed!');
        }

        if (!prevProps.refreshIntegrationsError && this.props.refreshIntegrationsError) {
            globalMessage.error(this.props.refreshIntegrationsError);
        }
    }

    updateIntegration = (
        integrationId: IntegrationInstallationId,
        integrationType: IntegrationType,
        data: UpdateIntegrationRequestDataType,
    ) => {
        const updateRequest: UpdateIntegrationRequest = {
            orgId: this.props.orgId,
            appId: this.props.appId,
            integrationId,
            data,
        };

        const type = integrationUpdatableForIntegrationType(integrationType);

        if (type) {
            this.props.updateIntegration(type, updateRequest);
        } else {
            globalMessage.error('This integration does not support updating.');
        }
    };

    addIntegration = (integrationType: IntegrationType, data: CreateIntegrationRequestDataType) => {
        const request: CreateIntegrationRequest = {
            orgId: this.props.orgId,
            appId: this.props.appId,
            integrationId: integrationType,
            data,
        };

        const authType = authTypeForIntegrationType(integrationType);

        if (authType) {
            this.props.addIntegration(authType, request);
        } else {
            globalMessage.error('Unrecognized auth type for integration type');
        }
    };

    deleteIntegration = (integrationId: IntegrationInstallationId, integrationType: IntegrationType) => {
        const request: IntegrationRequest = {
            orgId: this.props.orgId,
            appId: this.props.appId,
            integrationId,
        };

        const type = integrationDeletableForIntegrationType(integrationType);

        if (type) {
            this.props.deleteIntegration(type, request);
        } else {
            globalMessage.error('This integration does not support deletion.');
        }
    };

    updateIntegrationAlias = (integrationId: IntegrationInstallationId, alias: string) => {
        const request: UpdateIntegrationAliasRequest = {
            orgId: this.props.orgId,
            appId: this.props.appId,
            integrationId,
            alias,
        };
        this.props.updateIntegrationAlias(request);
    };

    render() {
        const normalizedSearchValue = normalizeSearchText(this.state.searchValue);
        const tagById = this.props.orgTags.reduce<Record<string, OrgTag>>((acc, tag) => {
            acc[tag.id] = tag;
            return acc;
        }, {});
        const destinationsByIntegrationId = destinationsByIntegrationInstallationId(
            this.props.stepGraph,
            this.props.destinationInfoByDestinationStepConfigId,
            this.props.orgTags,
        );
        const searchOptions = integrationSearchOptions(
            this.props.integrations,
            destinationsByIntegrationId,
            this.props.orgTags,
            this.state.searchValue,
        );
        const integrationMatchesSearch = (integration: Integration) => {
            if (!normalizedSearchValue) {
                return true;
            }

            return searchableTextForIntegration(
                integration,
                destinationsByIntegrationId[integration.installationId] ?? [],
                tagById,
            ).includes(normalizedSearchValue);
        };

        return (
            <div>
                {this.state.showConfigurationModal && this.state.integrationId && (
                    <IntegrationConfiguration
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        closable={this.state.closable}
                        isLoading={this.props.updateIntegrationIsLoading[this.state.integrationId]}
                        open={this.state.showConfigurationModal}
                        integration={this.props.integrations.find((i) => i.installationId === this.state.integrationId)}
                        updateIntegration={this.updateIntegration}
                        onClose={() => {
                            this.setState({
                                showConfigurationModal: false,
                                integrationId: null,
                            });
                        }}
                    />
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
                    <AutoComplete
                        value={this.state.searchValue}
                        options={searchOptions}
                        onSelect={(value) => this.setState({ searchValue: value })}
                        onChange={(value) => this.setState({ searchValue: value })}
                        style={{ width: 565, maxWidth: '100%' }}
                    >
                        <Input
                            allowClear
                            placeholder={'Search by integration, destination, tag'}
                            suffix={<SearchOutlined style={{ color: antGrey.grey7 }} />}
                        />
                    </AutoComplete>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 45 }}>
                    {this.props.integrationCapabilities.map((capability) => {
                        const integrationsWithCapability = this.props.integrations.filter((integration) => {
                            return integration.capabilities.includes(capability);
                        });
                        const filteredIntegrationsWithCapability =
                            normalizedSearchValue ?
                                integrationsWithCapability.filter(integrationMatchesSearch)
                            :   integrationsWithCapability;

                        if (normalizedSearchValue && filteredIntegrationsWithCapability.length === 0) {
                            return null;
                        }

                        const connectedIntegrationTypes = integrationsWithCapability.map(
                            (integration) => integration.id,
                        );

                        const hasOneConnectedIntegrationForCapability = filteredIntegrationsWithCapability.length > 0;
                        const allSupportedIntegrations = allSupportedIntegrationTypesForCapability(
                            capability,
                            this.props.isRunwayOrg,
                            this.props.orgId,
                            true,
                        );
                        const description = descriptionForCapability(capability);
                        if (!this.capabilitiesRefs[capability]) {
                            this.capabilitiesRefs[capability] = React.createRef();
                        }
                        return (
                            <div
                                key={capability}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    animationDuration: '0.5s',
                                    animationName: this.props.scrollTo === (capability as string) ? 'flash' : 'none',
                                }}
                                ref={this.capabilitiesRefs[capability]}
                            >
                                <Space direction="vertical" style={{ marginBottom: 42 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '22px' }}>
                                        <PlanRestrictedFeatureWrapper
                                            integrationCapability={capability}
                                            shouldFakeDisabledState
                                            requiresTooltip
                                        >
                                            <Title
                                                style={{
                                                    fontSize: '18px',
                                                    flexShrink: 0,
                                                    marginBottom: '0',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {displayNameForCapability(capability)}
                                            </Title>
                                        </PlanRestrictedFeatureWrapper>
                                        <Divider style={{ minWidth: 'auto' }} />
                                    </div>
                                    <Text type="secondary" style={{ maxWidth: '50%', marginBottom: 24, fontSize: 14 }}>
                                        {description}
                                    </Text>
                                </Space>

                                <>
                                    {hasOneConnectedIntegrationForCapability && (
                                        <PlanRestrictedFeatureWrapper integrationCapability={capability}>
                                            {(isDisabled) => (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'stretch',
                                                    }}
                                                >
                                                    <ConnectedIntegrationsComponent
                                                        orgId={this.props.orgId}
                                                        appId={this.props.appId}
                                                        integrations={filteredIntegrationsWithCapability}
                                                        destinationsByIntegrationInstallationId={
                                                            destinationsByIntegrationId
                                                        }
                                                        trackingProperties={this.props.trackingProperties}
                                                        updateIntegration={this.updateIntegration}
                                                        updateIntegrationIsLoading={
                                                            this.props.updateIntegrationIsLoading
                                                        }
                                                        updateIntegrationSuccess={this.props.updateIntegrationSucceeded}
                                                        updateIntegrationAlias={this.updateIntegrationAlias}
                                                        updateIntegrationAliasIsLoading={
                                                            this.props.updateIntegrationAliasIsLoading
                                                        }
                                                        updateIntegrationAliasFailed={
                                                            this.props.updateIntegrationAliasFailed
                                                        }
                                                        updateIntegrationAliasSucceeded={
                                                            this.props.updateIntegrationAliasSucceeded
                                                        }
                                                        showCompleteIntegrationsModal={(
                                                            integrationId: IntegrationInstallationId,
                                                        ) => {
                                                            this.setState({
                                                                showConfigurationModal: true,
                                                                integrationId: integrationId,
                                                                closable: true,
                                                            });
                                                        }}
                                                        deleteIntegration={this.deleteIntegration}
                                                        deleteIntegrationIsLoading={
                                                            this.props.deleteIntegrationIsLoading
                                                        }
                                                        capabilityDisabled={isDisabled}
                                                        capability={capability}
                                                    />
                                                    {!normalizedSearchValue && (
                                                        <Collapse defaultActiveKey={['1']} ghost>
                                                            <Panel header="More providers" key={capability}>
                                                                <IntegrationsForCapabilityComponent
                                                                    integrationCapability={capability}
                                                                    allSupportedIntegrationsForCapability={
                                                                        allSupportedIntegrations
                                                                    }
                                                                    connectedIntegrationsForCapability={
                                                                        connectedIntegrationTypes
                                                                    }
                                                                    isDisabled={isDisabled}
                                                                    orgId={this.props.orgId}
                                                                    appId={this.props.appId}
                                                                    integrationIsLoading={
                                                                        this.props.addIntegrationIsLoading
                                                                    }
                                                                    addIntegrationSuccess={
                                                                        this.props.addIntegrationSucceeded
                                                                    }
                                                                    addIntegration={this.addIntegration}
                                                                />
                                                            </Panel>
                                                        </Collapse>
                                                    )}
                                                </div>
                                            )}
                                        </PlanRestrictedFeatureWrapper>
                                    )}
                                    {!hasOneConnectedIntegrationForCapability && (
                                        <PlanRestrictedFeatureWrapper integrationCapability={capability}>
                                            {(isDisabled) => (
                                                <IntegrationsForCapabilityComponent
                                                    integrationCapability={capability}
                                                    allSupportedIntegrationsForCapability={allSupportedIntegrations}
                                                    connectedIntegrationsForCapability={connectedIntegrationTypes}
                                                    isDisabled={isDisabled}
                                                    orgId={this.props.orgId}
                                                    appId={this.props.appId}
                                                    integrationIsLoading={this.props.addIntegrationIsLoading}
                                                    addIntegrationSuccess={this.props.addIntegrationSucceeded}
                                                    addIntegration={this.addIntegration}
                                                />
                                            )}
                                        </PlanRestrictedFeatureWrapper>
                                    )}
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'stretch',
                                        }}
                                    >
                                        <div style={{ flexGrow: 1 }}>
                                            {capability === IntegrationCapability.VersionControl && (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 24,
                                                        marginTop: 24,
                                                    }}
                                                >
                                                    <ReleaseTagPatternsComponent
                                                        appId={this.props.appId}
                                                        orgId={this.props.orgId}
                                                        refProp={this.releaseTagPatterns}
                                                    />
                                                    <ReleaseBranchPatternsComponent
                                                        appId={this.props.appId}
                                                        orgId={this.props.orgId}
                                                        refProp={this.releaseBranchPatterns}
                                                    />
                                                    <AdditionalBranchesComponent
                                                        appId={this.props.appId}
                                                        orgId={this.props.orgId}
                                                    />
                                                </div>
                                            )}
                                            {capability === IntegrationCapability.FeatureManagement && (
                                                <FeatureAffiliationsComponent
                                                    appId={this.props.appId}
                                                    orgId={this.props.orgId}
                                                    refProp={this.featureAffiliations}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
}

interface IntegrationProps
    extends IntegrationRouteProps,
        RouteComponentProps<AppRouteComponentMatchParams, { statusCode }, AppSettingsLocationState> {} // TODO: refactor this component into a FC so location can be accessed via hook instead of passed down through props

const mapProps = (state: RootState, ownProps: IntegrationProps) => {
    const { appId, orgId, integrationType, history, location } = ownProps;

    const org = selectOrg(state, orgId);
    const app = selectApp(state, appId);
    const orgTags = org.tags ?? [];
    const destinationInfoByDestinationStepConfigId = selectDestinationInfoByDestinationStepConfigId(state, appId);
    const stepGraph = selectStepGraph(state, appId);
    const isRunwayOrg = selectIsRunwayOrg(state);
    const appsInOrg = getAllAppsInOrg(orgId, state).map((app) => app.appId);
    const appGroupsInOrg = getAppGroupsForOrgId(orgId, state).map((appGroup) => appGroup.name);

    const trackingProperties = allReleasePropertiesFrom(org, app, null, appsInOrg, appGroupsInOrg);

    const integrations = app.appSettings.integrations;
    const userId = state.user.id;
    const appSettingsIntegrationsRoute = createAppSettingsRouteWithSettingType(
        org.orgId,
        app.appId,
        SettingType.Integrations,
    );
    const appSettingsRoute = createAppSettingsRoute(org.orgId, app.appId);
    const scrollTo = location.state ? location.state.scrollTo : null;
    const integrationCapabilities = integrationCapabilitiesForPlatform(app.appSettings.platform, org.orgId);

    const addIntegrationIsLoading = {};
    const addIntegrationFailed = {};
    const addIntegrationSucceeded = {};

    const orgIdParam = org.orgId;
    const appIdParam = app.appId;

    const refreshIntegrationsIsLoading = getIsLoading(TRY_REFRESH_INTEGRATIONS, state);
    const refreshIntegrationsSuccess = getUpdated(TRY_REFRESH_INTEGRATIONS, state);
    const refreshIntegrationsError = getError(TRY_REFRESH_INTEGRATIONS, state);

    const deleteIntegrationSucceeded = {};

    AllIntegrationTypes.forEach((integrationType) => {
        const authType = authTypeForIntegrationType(integrationType);

        if (authType) {
            const isLoading = getIsLoading(authType, state);
            const failed = getError(authType, state);

            addIntegrationIsLoading[integrationType] = isLoading;
            addIntegrationFailed[integrationType] = failed;
        }

        const deleteType = integrationDeletableForIntegrationType(integrationType);
        if (deleteType) {
            // can't use the installation ID, if this succeeds, that installation is gone
            const updated = getUpdated(deleteType, state);
            deleteIntegrationSucceeded[integrationType] = updated;
        }
    });

    const deleteIntegrationIsLoading = {};
    const deleteIntegrationError = {};

    const updateIntegrationIsLoading = {};
    const updateIntegrationFailed = {};
    const updateIntegrationSucceeded = {};

    const updateIntegrationAliasIsLoading = {};
    const updateIntegrationAliasFailed = {};
    const updateIntegrationAliasSucceeded = {};

    integrations.forEach((integration) => {
        const updateType = integrationUpdatableForIntegrationType(integration.id);

        const updateIsLoading = getIsLoading(updateType, state, integration.installationId);
        const updateFailed = getError(updateType, state, integration.installationId);
        const updateSuccess = getUpdated(updateType, state, integration.installationId);
        updateIntegrationIsLoading[integration.installationId] = updateIsLoading;
        updateIntegrationFailed[integration.installationId] = updateFailed;
        updateIntegrationSucceeded[integration.installationId] = updateSuccess;

        const updateAliasIsLoading = getIsLoading(TRY_UPDATE_INTEGRATION_ALIAS, state, integration.installationId);
        const updateAliasFailed = getError(TRY_UPDATE_INTEGRATION_ALIAS, state, integration.installationId);
        const updateAliasSuccess = getUpdated(TRY_UPDATE_INTEGRATION_ALIAS, state, integration.installationId);
        updateIntegrationAliasIsLoading[integration.installationId] = updateAliasIsLoading;
        updateIntegrationAliasFailed[integration.installationId] = updateAliasFailed;
        updateIntegrationAliasSucceeded[integration.installationId] = updateAliasSuccess;

        const authType = authTypeForIntegrationType(integration.id);
        if (authType) {
            const updated = getUpdated(authType, state, integration.installationId);
            addIntegrationSucceeded[integration.installationId] = updated;
        }

        const deleteType = integrationDeletableForIntegrationType(integration.id);
        if (deleteType) {
            const isLoading = getIsLoading(deleteType, state, integration.installationId);
            const error = getError(deleteType, state, integration.installationId);

            deleteIntegrationIsLoading[integration.installationId] = isLoading;
            deleteIntegrationError[integration.installationId] = error;
        }
    });

    const isRedirect = integrationType !== undefined;
    const queryParameters = qs.parse(location.search);

    const integrationCreateOrRefreshRequestOrError: IntegrationCreateOrRefreshRequestOrError =
        isRedirect ?
            extractIntegrationRequestFromRedirect(
                integrationType,
                queryParameters,
                orgId,
                appId,
                orgIdParam,
                appIdParam,
            )
        :   null;

    return {
        integrations,
        orgTags,
        destinationInfoByDestinationStepConfigId,
        stepGraph,
        userId,
        orgId,
        appId,
        history,
        appSettingsIntegrationsRoute,
        integrationCreateOrRefreshRequestOrError,
        addIntegrationIsLoading: addIntegrationIsLoading,
        addIntegrationFailed: addIntegrationFailed,
        addIntegrationSucceeded: addIntegrationSucceeded,
        refreshIntegrationsIsLoading,
        refreshIntegrationsSuccess,
        refreshIntegrationsError,
        updateIntegrationFailed,
        updateIntegrationIsLoading,
        updateIntegrationSucceeded,
        updateIntegrationAliasIsLoading,
        updateIntegrationAliasFailed,
        updateIntegrationAliasSucceeded,
        deleteIntegrationError,
        deleteIntegrationIsLoading,
        deleteIntegrationSucceeded,
        scrollTo: scrollTo,
        integrationCapabilities,
        trackingProperties,
        appSettingsRoute,
        isRunwayOrg,
    };
};

const mapDispatch = (dispatch) => {
    return {
        addIntegration: (type: AuthUpdatable, request: CreateIntegrationRequest) => {
            dispatch(
                tryCreateIntegrationRequest(type, {
                    ...request,
                    isQuickStartCI: false,
                }),
            );
        },
        updateIntegration: (type: IntegrationUpdatable, request: UpdateIntegrationRequest) => {
            dispatch(tryUpdateIntegrationRequest(type, request));
        },
        refreshIntegration: (type: IntegrationRefreshable, request: RefreshIntegrationRequest) => {
            dispatch(tryRefreshIntegrationRequest(type, request));
        },
        deleteIntegration: (type: IntegrationDeletable, request: IntegrationRequest) => {
            dispatch(tryDeleteIntegration(type, request));
        },
        updateIntegrationAlias: (request: UpdateIntegrationAliasRequest) => {
            dispatch(tryUpdateIntegrationAlias(request));
        },
    };
};

const connector = connect(mapProps, mapDispatch);

type Props = ConnectedProps<typeof connector>;

export default connector(Integrations);
